import {
  LabelledUser,
  toOptionalUserWithPersonalData,
} from '../user/mapping/toView';
import type {
  CompanyAddressCountryFieldKey,
  CompanyAddressesOnView,
  CompanyAddressTextFieldKey,
  CompanyOnView,
} from 'shared/domain/company/types/view';
import {
  CompanyCreateOutDto,
  CompanyEditOutDto,
} from 'shared/dtos/out/company';
import { DateTime } from 'luxon';
import { IntlShape } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import { toDisplayedInvolvementType } from './mapping/toView';
import { toCountryOption } from 'shared/domain/country/countries';
import {
  CompanyAddress,
  CompanyAddressChanges,
  CompanyAddresses,
  CompanyAddressKind,
  companyAddressKinds,
  CompanyAddressTextPart,
  companyAddressTextParts,
  CompanyCreateModel,
  CompanyEditModel,
  CompanyModel,
  InvolvementType,
  involvementTypes,
} from './types/model';
import { UserWithPersonalData } from '../user/types/model';

export function companyModelToLabelledEntity(
  company: CompanyModel
): LabelledEntity {
  return {
    label: company.shortLabel,
    _id: company._id,
  };
}

/** The form field of an address text part, e.g. `correspondenceAddressPostalCode`. */
export function companyAddressTextFieldKey(
  kind: CompanyAddressKind,
  part: CompanyAddressTextPart
): CompanyAddressTextFieldKey {
  return `${kind}${part.charAt(0).toUpperCase()}${part.slice(1)}` as CompanyAddressTextFieldKey;
}

/** The form field of an address country, e.g. `correspondenceAddressCountry`. */
export function companyAddressCountryFieldKey(kind: CompanyAddressKind): CompanyAddressCountryFieldKey {
  return `${kind}Country`;
}

/** Whether any part of the address is filled in the form. */
export function isCompanyAddressStarted(kind: CompanyAddressKind, fields?: CompanyAddressesOnView): boolean {
  return (
    Boolean(fields?.[companyAddressCountryFieldKey(kind)]) ||
    companyAddressTextParts.some((part) => Boolean(fields?.[companyAddressTextFieldKey(kind, part)]))
  );
}

/** The addresses as the form's fields; `locale` names the countries. */
export function companyAddressesToOnView(addresses: CompanyAddresses, locale: string): CompanyAddressesOnView {
  const fields: CompanyAddressesOnView = {};
  companyAddressKinds.forEach((kind) => {
    const address = addresses[kind];
    companyAddressTextParts.forEach((part) => {
      fields[companyAddressTextFieldKey(kind, part)] = address?.[part];
    });
    fields[companyAddressCountryFieldKey(kind)] = address?.country
      ? toCountryOption(address.country, locale)
      : undefined;
  });
  return fields;
}

/** The address of the form's fields, or `undefined` when none of its parts is filled. */
function companyAddressOnViewToModel(
  fields: CompanyAddressesOnView,
  kind: CompanyAddressKind
): CompanyAddress | undefined {
  const address: CompanyAddress = {
    streetAndNumber: fields[companyAddressTextFieldKey(kind, 'streetAndNumber')],
    postalCode: fields[companyAddressTextFieldKey(kind, 'postalCode')],
    city: fields[companyAddressTextFieldKey(kind, 'city')],
    country: fields[companyAddressCountryFieldKey(kind)]?._id,
  };
  return Object.values(address).some(Boolean) ? address : undefined;
}

/** A create sends the filled addresses only (validation makes them complete). */
export function companyAddressesOnViewToCreateModel(fields: CompanyAddressesOnView): CompanyAddresses {
  return Object.fromEntries(
    companyAddressKinds.flatMap((kind) => {
      const address = companyAddressOnViewToModel(fields, kind);
      return address ? [[kind, address]] : [];
    })
  );
}

/** An edit sends every address: `null` removes one the form emptied. */
export function companyAddressesOnViewToEditModel(fields: CompanyAddressesOnView): CompanyAddressChanges {
  return Object.fromEntries(
    companyAddressKinds.map((kind) => [kind, companyAddressOnViewToModel(fields, kind) ?? null])
  );
}

export function companyOnViewToCompanyCreateModel(
  fields: CompanyOnView,
  projectId: string,
  author?: UserWithPersonalData
): CompanyCreateModel {
  return {
    longLabel: fields.longLabel,
    shortLabel: fields.shortLabel,
    deleted: fields.deleted,
    createdBy: author,
    modifiedBy: author,
    createdAt: fields.createdAt.toISO(),
    modifiedAt: fields.modifiedAt.toISO(),
    projectInvolvementType: fields.projectInvolvementType._id,
    sites: fields.sites.map((site) => site._id),
    taxNumber: fields.taxNumber,
    ...companyAddressesOnViewToCreateModel(fields),
    projectId,
  };
}

function textOnViewToTextOnModel(
  value: string | undefined | null
): string | undefined {
  if (value === null) {
    return '';
  }
  return value;
}

export function companyOnViewToCompanyEditModel(
  fields: CompanyOnView,
  projectId
): CompanyEditModel {
  return {
    shortLabel: fields.shortLabel,
    longLabel: fields.longLabel,
    _id: fields._id,
    // contracts: fields.contracts.map(contract => contract._id),
    projectInvolvementType: fields.projectInvolvementType._id,
    sites: fields.sites.map((site) => site._id),
    taxNumber: textOnViewToTextOnModel(fields.taxNumber),
    ...companyAddressesOnViewToEditModel(fields),
    projectId,
  };
}

export function companyEditModelToEditOutDto(
  model: CompanyEditModel
): CompanyEditOutDto {
  return {
    longLabel: model.longLabel,
    projectInvolvementType: model.projectInvolvementType,
    shortLabel: model.shortLabel,
    sites: model.sites,
    taxNumber: model.taxNumber,
    correspondenceAddress: model.correspondenceAddress,
    registeredOfficeAddress: model.registeredOfficeAddress,
  };
}

export function companyCreateModelToCreateOutDto(
  model: CompanyCreateModel
): CompanyCreateOutDto {
  return {
    longLabel: model.longLabel,
    projectInvolvementType: model.projectInvolvementType,
    shortLabel: model.shortLabel,
    sites: model.sites,
    taxNumber: model.taxNumber,
    correspondenceAddress: model.correspondenceAddress,
    registeredOfficeAddress: model.registeredOfficeAddress,
  };
}

export function toLabelledInvolvementType(
  involvementType: InvolvementType,
  intl: IntlShape
): { _id: InvolvementType; label: string } {
  return {
    _id: involvementType,
    label: toDisplayedInvolvementType(involvementType, intl),
  };
}

export function createEmptyCompany(
  intl: IntlShape,
  now: DateTime<true>,
  author: LabelledUser
): CompanyOnView {
  return {
    _id: '',
    longLabel: '',
    projectInvolvementType: toLabelledInvolvementType(
      involvementTypes[0],
      intl
    ),
    shortLabel: '',
    sites: [],
    taxNumber: undefined,
    ...companyAddressesToOnView({}, intl.locale),
    deleted: false,
    createdAt: now,
    modifiedAt: now,
    modifiedBy: author,
    createdBy: author,
  };
}
